import { useEffect, useState } from "react";
import { faro } from "@grafana/faro-web-sdk";
import { toast } from "sonner";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type * as API from "@shared-core/data/api-types";
import { SlideOverPanel } from "../../patterns/SlideOverPanel";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { Textarea } from "../../primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";

/**
 * ReplyComposer — CSR command-centre Slice 3 (draft-in-Outlook quick
 * replies, see docs/features/csr-command-centre/). From a job (especially a
 * late one) or a specific email thread, the CSR picks a starter template,
 * edits the prefilled subject/body, then creates a draft in their own
 * Outlook — the system NEVER sends; the CSR reviews and sends it themselves
 * from Outlook. Renders as a drawer (`SlideOverPanel`) — the only shell; an
 * earlier dev-only inline variant was retired once the drawer shell was
 * settled.
 *
 * Backend-driven end to end: on open, `listReplyTemplates` fills the
 * template dropdown and `prepareEmailDraft` renders the default template
 * into a prefilled subject/body/recipient (server-owned copy — see
 * `_reply_templates.py`). Switching templates re-runs `prepareEmailDraft`
 * and overwrites the draft — deliberately simple per the product brief:
 * switching templates overwrites in-progress edits rather than trying to
 * merge them. "Create draft in Outlook" sends the (possibly CSR-edited)
 * subject/body to `createEmailDraft`.
 */

export interface ReplyComposerJobContext {
  jobNo: string;
  jobTitle: string;
  customerName: string;
  customerId: string;
  requiredAt: string | null;
  /** "green" | "amber" | "red" in practice. Typed as ``string`` because
   *  ``WorkUnitSummaryItem.rag`` (the portfolio-board source) isn't a
   *  literal union in the generated schema — see ``defaultTemplateId``. */
  rag: string;
}

/** A known recipient hint for the per-email "Draft reply" journey — the
 *  sender of the email being replied to. Omitted for a job-level "Draft
 *  update", where the backend resolves a default recipient itself (see
 *  `prepareEmailDraft`/`createEmailDraft`'s `to_address`/`to_name`
 *  fallback). */
export interface ReplyComposerContact {
  name: string;
  email: string;
}

export interface ReplyComposerInReplyTo {
  /** Underlying email id, passed through as `in_reply_to_email_id`. */
  id: string;
}

export interface ReplyComposerProps {
  job: ReplyComposerJobContext;
  /** The email being replied to's sender — per-email "Draft reply" only.
   *  Undefined for a job-level "Draft update". */
  to?: ReplyComposerContact;
  /** The email being replied to, when opened from a specific thread. */
  inReplyTo?: ReplyComposerInReplyTo | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Late/at-risk jobs default to the late-job template; everything else
 *  defaults to ready-for-dispatch. The CSR can always pick a different
 *  template (including "Chase for artwork") from the template dropdown.
 *  A UI default choice, not business copy — the template ids/labels/bodies
 *  themselves are entirely backend-owned. */
function defaultTemplateId(rag: string): string {
  return rag === "red" || rag === "amber" ? "late_job" : "ready_for_dispatch";
}

export function ReplyComposer({
  job,
  to,
  inReplyTo,
  open,
  onOpenChange,
}: ReplyComposerProps) {
  const dataAdapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [templates, setTemplates] = useState<API.ReplyTemplateSummary[]>([]);
  const [templateId, setTemplateId] = useState<string>(() =>
    defaultTemplateId(job.rag),
  );
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [toAddress, setToAddress] = useState<string | null>(null);
  const [toName, setToName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function buildPrepareInput(id: string): API.PrepareEmailDraftBody {
    return {
      template_id: id,
      job_no: job.jobNo,
      customer_id: job.customerId,
      customer_name: job.customerName,
      job_title: job.jobTitle,
      required_at: job.requiredAt,
      to_address: to?.email,
      to_name: to?.name,
      in_reply_to_email_id: inReplyTo?.id ?? null,
    };
  }

  function applyPrepared(prepared: API.PrepareEmailDraftResponse) {
    setTemplateId(prepared.template_id);
    setSubject(prepared.subject);
    setBody(prepared.body);
    setToAddress(prepared.to_address);
    setToName(prepared.to_name);
  }

  // Load the template dropdown + the default template's prefill whenever
  // the composer opens for a (possibly new) job or reply target — reopening
  // starts fresh.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const initialId = defaultTemplateId(job.rag);
    setTemplateId(initialId);
    setLoading(true);
    (async () => {
      try {
        const [templateOptions, prepared] = await Promise.all([
          dataAdapter.listReplyTemplates(),
          dataAdapter.prepareEmailDraft(buildPrepareInput(initialId)),
        ]);
        if (cancelled) return;
        setTemplates(templateOptions);
        applyPrepared(prepared);
      } catch (err) {
        if (cancelled) return;
        toast.error("Couldn't prepare this draft — try again.");
        faro.api?.pushError(
          err instanceof Error
            ? err
            : new Error("reply_composer.load_draft.failed"),
          { context: { job_no: job.jobNo } },
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- buildPrepareInput closes over job/to/inReplyTo, the real deps
  }, [open, job.jobNo, to?.email, inReplyTo?.id]);

  async function handleTemplateChange(id: string) {
    setTemplateId(id);
    setPreparing(true);
    try {
      const prepared = await dataAdapter.prepareEmailDraft(
        buildPrepareInput(id),
      );
      applyPrepared(prepared);
    } catch (err) {
      toast.error("Couldn't switch templates — try again.");
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("reply_composer.switch_template.failed"),
        { context: { job_no: job.jobNo, template_id: id } },
      );
    } finally {
      setPreparing(false);
    }
  }

  async function handleCreateDraft() {
    setSubmitting(true);
    try {
      const result = await dataAdapter.createEmailDraft({
        template_id: templateId,
        job_no: job.jobNo,
        customer_id: job.customerId,
        job_title: job.jobTitle,
        required_at: job.requiredAt,
        to_address: to?.email,
        to_name: to?.name,
        in_reply_to_email_id: inReplyTo?.id ?? null,
        subject,
        body,
      });
      toast.success("Draft created in your Outlook", {
        action: result.web_link
          ? {
              label: "Open in Outlook",
              onClick: () => capabilities.openExternalUrl(result.web_link!),
            }
          : undefined,
      });
      onOpenChange(false);
    } catch (err) {
      toast.error("Couldn't create the draft — try again.");
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("reply_composer.create_draft.failed"),
        { context: { job_no: job.jobNo } },
      );
    } finally {
      setSubmitting(false);
    }
  }

  const title = inReplyTo ? "Draft reply" : "Draft update to customer";
  const busy = loading || preparing || submitting;

  return (
    <SlideOverPanel
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      footer={
        <div className="flex justify-end gap-2 w-full">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleCreateDraft} disabled={busy}>
            {submitting ? "Creating draft…" : "Create draft in Outlook"}
          </Button>
        </div>
      }
    >
      {loading ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          Preparing your draft…
        </p>
      ) : (
        <ReplyComposerForm
          templates={templates}
          templateId={templateId}
          toAddress={toAddress}
          toName={toName}
          subject={subject}
          body={body}
          disabled={preparing || submitting}
          onTemplateChange={handleTemplateChange}
          onSubjectChange={setSubject}
          onBodyChange={setBody}
        />
      )}
    </SlideOverPanel>
  );
}

function ReplyComposerForm({
  templates,
  templateId,
  toAddress,
  toName,
  subject,
  body,
  disabled,
  onTemplateChange,
  onSubjectChange,
  onBodyChange,
}: {
  templates: API.ReplyTemplateSummary[];
  templateId: string;
  toAddress: string | null;
  toName: string | null;
  subject: string;
  body: string;
  disabled: boolean;
  onTemplateChange: (id: string) => void;
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="reply-composer-template">Template</Label>
        {/* A dropdown rather than a toggle group so the composer's height
         *  stays fixed as more templates are added. */}
        <Select
          value={templateId}
          onValueChange={onTemplateChange}
          disabled={disabled}
        >
          <SelectTrigger id="reply-composer-template" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {templates.map((template) => (
              <SelectItem key={template.id} value={template.id}>
                {template.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label>To</Label>
        <p className="text-sm text-muted-foreground px-3 py-2 rounded-md border border-input bg-muted/30">
          {toAddress
            ? `${toName ? `${toName} ` : ""}<${toAddress}>`
            : "No recipient resolved — add one in Outlook"}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="reply-composer-subject">Subject</Label>
        <Input
          id="reply-composer-subject"
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          disabled={disabled}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="reply-composer-body">Message</Label>
        <Textarea
          id="reply-composer-body"
          value={body}
          onChange={(e) => onBodyChange(e.target.value)}
          className="min-h-[320px]"
          disabled={disabled}
        />
      </div>
    </div>
  );
}
